import { useState, useEffect, CSSProperties } from "react";
import I18n from "common/i18n";
import { getPlanColor } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { PLAN_COMPARISON_ITEMS } from "features/user/constants";
import {
  PlanType,
  PlanCategory,
  PlanLimits,
  QuotaStatus,
  PLAN_LIMITS,
  usePlanLimits,
} from "features/subscription";

const planCategoryToType = (category: PlanCategory): PlanType =>
  category === "standard" ? PlanType.STANDARD : PlanType.PREMIUM;

const PLAN_NAME_KEY: Record<PlanType, string> = {
  [PlanType.TRIAL]: "MSG_PLAN_TRIAL",
  [PlanType.FREE]: "MSG_PLAN_FREE",
  [PlanType.STANDARD]: "MSG_PLAN_STANDARD_FULL",
  [PlanType.PREMIUM]: "MSG_PLAN_PREMIUM_FULL",
  [PlanType.BUILDER]: "MSG_PLAN_BUILDER",
  [PlanType.EDUCATION]: "MSG_PLAN_EDUCATION",
};

const pickDefaultCategory = (availablePlans: PlanCategory[]): PlanCategory => availablePlans[0];

type FormattedQuota =
  | { kind: "special"; text: string }
  | { kind: "parts"; prefix: string; value: string; unit: string };

const splitBytes = (limit: number): { value: string; unit: string } => {
  if (limit >= 1073741824) return { value: String(Math.round(limit / 1073741824)), unit: "GB" };
  if (limit >= 1048576) return { value: String(Math.round(limit / 1048576)), unit: "MB" };
  return { value: String(limit), unit: "B" };
};

const formatQuotaParts = (
  quota: QuotaStatus,
  format?: "bytes",
  unitKey?: string,
  periodPrefixKey?: string,
): FormattedQuota => {
  if (quota.accessDenied) return { kind: "special", text: "—" };
  if (quota.unlimited) return { kind: "special", text: I18n.t("MSG_USER_PLAN_UNLIMITED") };
  if (format === "bytes") {
    const { value, unit } = splitBytes(quota.limit);
    return { kind: "parts", prefix: "", value, unit };
  }
  const value = String(quota.limit);
  const unit = unitKey ? I18n.t(unitKey) : "";
  const prefix = periodPrefixKey ? I18n.t(periodPrefixKey) : "";
  return { kind: "parts", prefix, value, unit };
};

type QuotaTone = "current" | "upgrade";

const QuotaText = ({ formatted, tone }: { formatted: FormattedQuota; tone: QuotaTone }) => {
  const toneClass =
    tone === "current" ? "text-textcolor-black/65" : "text-[color:var(--plan-accent)]";
  if (formatted.kind === "special") {
    return (
      <p className={`type-text type-body leading-tight whitespace-nowrap ${toneClass}`}>
        {formatted.text}
      </p>
    );
  }
  return (
    <div className="flex-row-el items-baseline gap-1">
      {formatted.prefix && (
        <span className={`type-text type-sm leading-tight whitespace-nowrap ${toneClass}`}>
          {formatted.prefix}
        </span>
      )}
      <span className={`type-text type-title leading-tight whitespace-nowrap ${toneClass}`}>
        {formatted.value}
      </span>
      {formatted.unit && (
        <span className={`type-text type-sm leading-tight whitespace-nowrap ${toneClass}`}>
          {formatted.unit}
        </span>
      )}
    </div>
  );
};

const UpgradeRow = ({
  icon,
  label,
  color,
  format,
  unitKey,
  periodPrefixKey,
  currentQuota,
  upgradeQuota,
}: {
  icon: string;
  label: string;
  color: string;
  format?: "bytes";
  unitKey?: string;
  periodPrefixKey?: string;
  currentQuota: QuotaStatus;
  upgradeQuota: QuotaStatus;
}) => {
  const currentFormatted = formatQuotaParts(currentQuota, format, unitKey, periodPrefixKey);
  const upgradeFormatted = formatQuotaParts(upgradeQuota, format, unitKey, periodPrefixKey);

  return (
    <div
      className="flex-row-el flex-1 min-h-0 items-center gap-3 w-full"
      style={{ "--row-icon": color } as CSSProperties}
    >
      <span className="material-symbols-outlined type-hero shrink-0 text-[color:var(--row-icon)]">
        {icon}
      </span>
      <p className="flex-1 type-text type-body text-textcolor-black leading-tight truncate">
        {label}
      </p>
      <div className="flex-row-el items-center justify-center shrink-0 w-[140px]">
        <div className="flex-row-el items-center justify-center rounded-xl py-2 px-3 w-full bg-gray2-40">
          <QuotaText formatted={currentFormatted} tone="current" />
        </div>
      </div>
      <span className="material-symbols-outlined type-md shrink-0 leading-none text-[color:var(--plan-accent)]">
        arrow_forward
      </span>
      <div className="flex-row-el items-center justify-center shrink-0 w-[160px]">
        <div className="flex-row-el items-center justify-center rounded-xl py-2 px-3 w-full bg-[color:var(--plan-accent)]/15">
          <QuotaText formatted={upgradeFormatted} tone="upgrade" />
        </div>
      </div>
    </div>
  );
};

const PlanSwitcher = ({
  availablePlans,
  selected,
  onChange,
}: {
  availablePlans: PlanCategory[];
  selected: PlanCategory;
  onChange: (category: PlanCategory) => void;
}) => {
  const play = usePlaySound();
  const handleCycle = () => {
    if (availablePlans.length <= 1) return;
    const idx = availablePlans.indexOf(selected);
    const next = availablePlans[idx < 0 ? 0 : (idx + 1) % availablePlans.length];
    if (next === selected) return;
    play();
    btnDelay(() => onChange(next));
  };

  const plan = planCategoryToType(selected);

  return (
    <button
      type="button"
      onClick={handleCycle}
      className="type-inherit-font flex-row-el items-center justify-center shrink-0 gap-2 w-[180px] rounded-full py-1 px-4 bg-white border-2 border-solid border-[color:var(--plan-accent)] shadow-[0_3px_0_rgba(63,74,97,0.14)] active:translate-y-[2px] active:shadow-[0_1px_0_rgba(63,74,97,0.14)] transition-transform cursor-pointer"
    >
      <span className="material-symbols-outlined type-md shrink-0 leading-none text-[color:var(--plan-accent)]">
        cached
      </span>
      <span className="type-text type-sm leading-tight whitespace-nowrap text-[color:var(--plan-accent)]">
        {I18n.t(PLAN_NAME_KEY[plan])}
      </span>
    </button>
  );
};

type Props = {
  availablePlans: PlanCategory[];
};

export const UserPlanUpgradePreviewCard = ({ availablePlans }: Props) => {
  const limits: PlanLimits = usePlanLimits();
  const [selectedCategory, setSelectedCategory] = useState<PlanCategory>(() =>
    pickDefaultCategory(availablePlans),
  );

  useEffect(() => {
    if (!availablePlans.includes(selectedCategory) && availablePlans.length > 0) {
      setSelectedCategory(pickDefaultCategory(availablePlans));
    }
  }, [availablePlans, selectedCategory]);

  const targetPlan = planCategoryToType(selectedCategory);
  const upgradeLimits = PLAN_LIMITS[targetPlan];
  const accent = getPlanColor(targetPlan);
  const showSwitcher = availablePlans.length > 1;

  return (
    <div
      className="flex-col-el flex-1 min-h-0 rounded-2xl bg-white border-2 border-solid border-[color:var(--plan-accent)] p-4 gap-3 overflow-hidden"
      style={{ "--plan-accent": accent } as CSSProperties}
    >
      <div className="flex-row-el items-start justify-between gap-3 shrink-0">
        <div className="flex-col-el items-start min-w-0">
          <p className="type-text type-title leading-tight text-[color:var(--plan-accent)]">
            {I18n.t("MSG_USER_PLAN_UPGRADE_PREVIEW_TITLE")}
          </p>
          <p className="type-text type-sm text-textcolor-black/60 leading-tight">
            {I18n.t("MSG_USER_PLAN_UPGRADE_PREVIEW_SUBTITLE")}
          </p>
        </div>
        {showSwitcher && (
          <PlanSwitcher
            availablePlans={availablePlans}
            selected={selectedCategory}
            onChange={setSelectedCategory}
          />
        )}
      </div>
      <div className="flex-col-el flex-1 min-h-0 justify-around gap-2">
        {PLAN_COMPARISON_ITEMS.filter((item) => item.key !== "aiImagePerRequest").map((item) => (
          <UpgradeRow
            key={item.key}
            icon={item.icon}
            label={I18n.t(item.labelKey)}
            color={item.color}
            format={item.format}
            unitKey={item.unitKey}
            periodPrefixKey={item.periodPrefixKey}
            currentQuota={limits[item.key]}
            upgradeQuota={upgradeLimits[item.key]}
          />
        ))}
      </div>
    </div>
  );
};
